import React from 'react';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { readQuizAudioPrefs, writeQuizAudioPrefs } from '@/utils/quizAudioPrefs';

/**
 * Bật/tắt + âm lượng khi làm bài quiz công khai (/play/...).
 * `variant="compact"` — popover icon cài đặt (topbar); mặc định — trang /settings.
 * Lưu localStorage (trình duyệt).
 */
export default function QuizAudioPrefsPanel({ className = '', id: sectionId, variant = 'default' }) {
  const { t } = useAppTranslation();
  const compact = variant === 'compact';
  const [p, setP] = React.useState(() => readQuizAudioPrefs());
  const { sfxOn, musicOn, sfxVol, musicVol } = p;

  const persist = React.useCallback((patch) => {
    writeQuizAudioPrefs(patch);
    setP(readQuizAudioPrefs());
  }, []);

  const gap = compact ? 'space-y-3' : 'space-y-4';
  const labelClass = compact
    ? 'text-sm font-medium text-slate-800 dark:text-slate-100'
    : 'text-sm font-medium text-slate-800 dark:text-slate-100';

  return (
    <div
      id={sectionId}
      className={
        compact
          ? `bg-transparent ${className}`
          : `rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900 ${className}`
      }
    >
      <div className={gap}>
        <div className="space-y-2">
          <label className={`flex cursor-pointer items-center gap-2 ${labelClass}`}>
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              checked={sfxOn}
              onChange={(e) => persist({ sfxOn: e.target.checked })}
            />
            {t('settings.audioSfx')}
          </label>
          <div className="flex items-center gap-2 pl-6">
            <input
              type="range"
              min={0}
              max={100}
              value={sfxVol}
              disabled={!sfxOn}
              onChange={(e) => persist({ sfxVol: Number(e.target.value) })}
              className="min-w-0 flex-1 accent-cyan-600 disabled:opacity-40"
              aria-label={t('settings.audioSfxVolAria')}
            />
            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">
              {sfxVol}%
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <label className={`flex cursor-pointer items-center gap-2 ${labelClass}`}>
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              checked={musicOn}
              onChange={(e) => persist({ musicOn: e.target.checked })}
            />
            {t('settings.audioMusic')}
          </label>
          <div className="flex items-center gap-2 pl-6">
            <input
              type="range"
              min={0}
              max={100}
              value={musicVol}
              disabled={!musicOn}
              onChange={(e) => persist({ musicVol: Number(e.target.value) })}
              className="min-w-0 flex-1 accent-emerald-600 disabled:opacity-40"
              aria-label={t('settings.audioMusicVolAria')}
            />
            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">
              {musicVol}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
